
import React, { useEffect } from 'react';
import { MenuItem } from '@/types/menu';
import { X } from 'lucide-react';
import MenuImage from '../core/MenuImage';
import { trackMenuItemClick } from '@/services/analyticsService';

interface MenuItemPopupProps {
  item: MenuItem;
  restaurantId?: string;
  primaryColor?: string;
  isHappyHour?: boolean;
  onClose: () => void;
}

export const MenuItemPopup: React.FC<MenuItemPopupProps> = ({ 
  item, 
  restaurantId = '',
  primaryColor = '#8B5CF6',
  isHappyHour = false,
  onClose 
}) => {
  const hasHappyHourPrice = item.is_happy_hour && isHappyHour && item.happy_hour_price !== null;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleOverlayTouch = (e: React.TouchEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleItemClick = () => {
    if (restaurantId) {
      trackMenuItemClick(restaurantId, item.id);
    }
  };

  useEffect(() => {
    handleItemClick();
    
    // Empêcher le scroll du body quand la popup est ouverte
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleOverlayClick}
      onTouchStart={handleOverlayTouch}
    >
      <div 
        className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-auto relative"
        onClick={handleClick}
      >
        {/* Header avec bouton fermer */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-xl font-bold pr-8">{item.name}</h2>
          <button 
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 transition-colors p-2"
          >
            <X size={24} />
          </button>
        </div>

        {/* Contenu popup */}
        <div className="p-6">
          {/* Image grande */}
          {item.image && (
            <div className="w-full h-48 bg-gray-200 rounded-lg overflow-hidden mb-4">
              <MenuImage
                src={item.image}
                size="full"
                alt={item.name}
                className="w-full h-full"
                aspectRatio={1}
                objectFit="cover"
                fallbackText={item.name}
              />
            </div>
          )}

          {/* Description */}
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Description</h3>
            <p className="text-gray-700">{item.description}</p>
          </div>

          {/* Prix */}
          <div className="mb-4">
            {hasHappyHourPrice ? (
              <div className="flex items-center gap-3">
                <span className="line-through text-gray-400 text-lg">{item.price.toFixed(2)}€</span>
                <span className="text-2xl font-bold" style={{ color: primaryColor }}>
                  {item.happy_hour_price?.toFixed(2)}€
                </span>
                <span className="text-sm text-orange-600 font-semibold">Happy Hour!</span>
              </div>
            ) : (
              <span className="text-2xl font-bold text-green-600">{item.price.toFixed(2)}€</span>
            )}
          </div>

          {/* Allergènes */}
          {item.allergens && item.allergens.length > 0 && (
            <div className="mb-4">
              <h3 className="font-semibold mb-2 text-red-600">⚠️ Allergènes</h3>
              <div className="flex flex-wrap gap-2">
                {item.allergens.map((allergen, index) => (
                  <span 
                    key={index}
                    className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium"
                  >
                    {allergen}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Happy Hour Info */}
          {item.is_happy_hour && (
            <div className="mb-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
              <h3 className="font-semibold text-orange-800 mb-1">🍹 Article Happy Hour</h3>
              <p className="text-sm text-orange-700">
                Cet article bénéficie d'un prix spécial pendant les heures happy hour.
              </p>
            </div>
          )}

          {/* Stock status */}
          {item.out_of_stock && (
            <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
              <span className="font-semibold text-red-800">❌ Actuellement épuisé</span>
            </div>
          )}
        </div>

        {/* Footer avec actions */}
        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-center">
          <button 
            onClick={onClose}
            className="px-8 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};
